import { RoleEnum } from '@/types/auth/roles';
import { User } from '@/types/auth/index';
import { hasTraderFullAccess } from './traderAccess';
import { hasExceptionalAppealSuccessAccess } from './appealSuccessAccess';
import { hasRevokedAccess } from './revokedAccess';
import { hasExceptionalTransactionSuccessAccess } from './transactionSuccessAccess';
import { hasPermission, Permission } from './permissions';

export enum ActionKey {
  USERS_TOGGLE_ACTIVE = 'USERS_TOGGLE_ACTIVE',
  USERS_TOGGLE_SUPERUSER = 'USERS_TOGGLE_SUPERUSER',
  USERS_UPDATE = 'USERS_UPDATE',
  USERS_DELETE = 'USERS_DELETE',

  // Финансы — Платёжные системы (кошельки)
  FINANCE_WALLETS_CREATE = 'FINANCE_WALLETS_CREATE',
  FINANCE_WALLETS_UPDATE = 'FINANCE_WALLETS_UPDATE',
  FINANCE_WALLETS_DELETE = 'FINANCE_WALLETS_DELETE',

  // Финансы — Банки (счета)
  FINANCE_ACCOUNTS_CREATE = 'FINANCE_ACCOUNTS_CREATE',
  FINANCE_ACCOUNTS_UPDATE = 'FINANCE_ACCOUNTS_UPDATE',
  FINANCE_ACCOUNTS_DELETE = 'FINANCE_ACCOUNTS_DELETE',

  // Финансы — Валюты
  FINANCE_CURRENCIES_CREATE = 'FINANCE_CURRENCIES_CREATE',
  FINANCE_CURRENCIES_UPDATE = 'FINANCE_CURRENCIES_UPDATE',
  FINANCE_CURRENCIES_DELETE = 'FINANCE_CURRENCIES_DELETE',

  // Финансы — Страны
  FINANCE_COUNTRIES_CREATE = 'FINANCE_COUNTRIES_CREATE',
  FINANCE_COUNTRIES_UPDATE = 'FINANCE_COUNTRIES_UPDATE',
  FINANCE_COUNTRIES_DELETE = 'FINANCE_COUNTRIES_DELETE',

  // Финансы — Курсы валют
  FINANCE_EXCHANGE_RATES_CREATE = 'FINANCE_EXCHANGE_RATES_CREATE',
  FINANCE_EXCHANGE_RATES_DELETE = 'FINANCE_EXCHANGE_RATES_DELETE',

  // Мерчанты
  MERCHANTS_CREATE = 'MERCHANTS_CREATE',
  MERCHANTS_UPDATE = 'MERCHANTS_UPDATE',
  MERCHANTS_DELETE = 'MERCHANTS_DELETE',

  // Балансы мерчантов
  MERCHANT_BALANCES_CREATE = 'MERCHANT_BALANCES_CREATE',
  MERCHANT_BALANCES_UPDATE = 'MERCHANT_BALANCES_UPDATE',
  MERCHANT_BALANCES_DELETE = 'MERCHANT_BALANCES_DELETE',

  // Курсы мерчантов
  MERCHANT_RATES_CREATE = 'MERCHANT_RATES_CREATE',
  MERCHANT_RATES_TOGGLE_ACTIVE = 'MERCHANT_RATES_TOGGLE_ACTIVE',
  MERCHANT_RATES_UPDATE = 'MERCHANT_RATES_UPDATE',
  MERCHANT_RATES_DELETE = 'MERCHANT_RATES_DELETE',

  // Офферы
  OFFERS_CREATE = 'OFFERS_CREATE',
  OFFERS_UPDATE = 'OFFERS_UPDATE',
  OFFERS_DELETE = 'OFFERS_DELETE',

  // Потери
  LOSSES_CREATE = 'LOSSES_CREATE',
  LOSSES_UPDATE = 'LOSSES_UPDATE',

  // Трейдеры
  TRADERS_CREATE = 'TRADERS_CREATE',
  TRADERS_TOGGLE_ACTIVE = 'TRADERS_TOGGLE_ACTIVE',
  /** Доступ к пулу выплат OUT (payout_out_enabled): только ADMIN / is_superuser. */
  TRADERS_PAYOUT_OUT_ENABLED = 'TRADERS_PAYOUT_OUT_ENABLED',
  TRADERS_UPDATE = 'TRADERS_UPDATE',
  TRADERS_DELETE = 'TRADERS_DELETE',

  // Балансы трейдеров
  TRADER_BALANCES_CREATE = 'TRADER_BALANCES_CREATE',
  TRADER_BALANCES_UPDATE = 'TRADER_BALANCES_UPDATE',
  TRADER_BALANCES_DELETE = 'TRADER_BALANCES_DELETE',
  TRADER_BALANCES_ADD_FUNDS = 'TRADER_BALANCES_ADD_FUNDS',

  // Курсы трейдеров
  TRADER_RATES_CREATE = 'TRADER_RATES_CREATE',
  TRADER_RATES_TOGGLE_ACTIVE = 'TRADER_RATES_TOGGLE_ACTIVE',
  TRADER_RATES_UPDATE = 'TRADER_RATES_UPDATE',
  TRADER_RATES_DELETE = 'TRADER_RATES_DELETE',

  /** Вердикт по реквизитам заявки OUT (UNDER_REVIEW). */
  PAYOUTS_OUT_INVOICE_REQUISITES_VERDICT = 'PAYOUTS_OUT_INVOICE_REQUISITES_VERDICT',
  PAYOUTS_OUT_RESOLVE_SUCCESS = 'PAYOUTS_OUT_RESOLVE_SUCCESS',
  PAYOUTS_OUT_RESOLVE_DECLINE = 'PAYOUTS_OUT_RESOLVE_DECLINE',

  // Транзакции
  TRANSACTIONS_VIEW_DETAILS = 'TRANSACTIONS_VIEW_DETAILS',
  TRANSACTIONS_UPDATE_TO_SUCCESS = 'TRANSACTIONS_UPDATE_TO_SUCCESS',
  TRANSACTIONS_IN_UPDATE_TO_SUCCESS = 'TRANSACTIONS_IN_UPDATE_TO_SUCCESS',
  TRANSACTIONS_UPDATE_TO_DECLINE = 'TRANSACTIONS_UPDATE_TO_DECLINE',
  TRANSACTIONS_SEND_CALLBACK = 'TRANSACTIONS_SEND_CALLBACK',
  APPEALS_RESOLVE_SUCCESS = 'APPEALS_RESOLVE_SUCCESS',

  // Реквизиты
  REQUISITES_CREATE = 'REQUISITES_CREATE',
  REQUISITES_TOGGLE_ACTIVE = 'REQUISITES_TOGGLE_ACTIVE',
  REQUISITES_UPDATE = 'REQUISITES_UPDATE',
  REQUISITES_DELETE = 'REQUISITES_DELETE',
  REQUISITES_UNBLOCK = 'REQUISITES_UNBLOCK',
}

export const ACTION_ACCESS_BY_ROLE: Record<ActionKey, RoleEnum[]> = {
  [ActionKey.USERS_TOGGLE_ACTIVE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  // Намеренно пусто: SUPPORT не может выдавать права администратора.
  [ActionKey.USERS_TOGGLE_SUPERUSER]: [],
  // PRODUCT_MANAGER может редактировать/удалять пользователей, но не может выдавать админские права.
  [ActionKey.USERS_UPDATE]: [RoleEnum.PRODUCT_MANAGER],
  [ActionKey.USERS_DELETE]: [RoleEnum.PRODUCT_MANAGER],

  // Мерчанты: создание доступно только ADMIN / is_superuser.
  [ActionKey.MERCHANTS_CREATE]: [RoleEnum.CLIENT_MANAGER],
  [ActionKey.MERCHANTS_UPDATE]: [RoleEnum.CLIENT_MANAGER],
  [ActionKey.MERCHANTS_DELETE]: [RoleEnum.CLIENT_MANAGER],

  // Балансы мерчантов: SUPPORT может создавать; редактирование и удаление запрещены.
  [ActionKey.MERCHANT_BALANCES_CREATE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER, RoleEnum.CLIENT_MANAGER],
  [ActionKey.MERCHANT_BALANCES_UPDATE]: [RoleEnum.CLIENT_MANAGER],
  [ActionKey.MERCHANT_BALANCES_DELETE]: [RoleEnum.CLIENT_MANAGER],

  // Курсы мерчантов: создание доступно только ADMIN / is_superuser.
  [ActionKey.MERCHANT_RATES_CREATE]: [RoleEnum.CLIENT_MANAGER],
  [ActionKey.MERCHANT_RATES_TOGGLE_ACTIVE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER, RoleEnum.CLIENT_MANAGER],
  [ActionKey.MERCHANT_RATES_UPDATE]: [RoleEnum.CLIENT_MANAGER],
  [ActionKey.MERCHANT_RATES_DELETE]: [RoleEnum.CLIENT_MANAGER],

  [ActionKey.OFFERS_CREATE]: [RoleEnum.CLIENT_MANAGER],
  [ActionKey.OFFERS_UPDATE]: [RoleEnum.CLIENT_MANAGER],
  [ActionKey.OFFERS_DELETE]: [RoleEnum.CLIENT_MANAGER],

  [ActionKey.LOSSES_CREATE]: [],
  [ActionKey.LOSSES_UPDATE]: [],

  // Трейдеры: PRODUCT_MANAGER имеет расширенные права (как у администратора по этой странице).
  [ActionKey.TRADERS_CREATE]: [RoleEnum.PRODUCT_MANAGER],
  [ActionKey.TRADERS_TOGGLE_ACTIVE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  // SUPPORT и PRODUCT_MANAGER управляют доступом трейдера к выплатам OUT и его лимитами.
  [ActionKey.TRADERS_PAYOUT_OUT_ENABLED]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [ActionKey.TRADERS_UPDATE]: [RoleEnum.PRODUCT_MANAGER],
  [ActionKey.TRADERS_DELETE]: [RoleEnum.PRODUCT_MANAGER],

  // Балансы трейдеров: для PRODUCT_MANAGER прав нет (по требованию).
  [ActionKey.TRADER_BALANCES_CREATE]: [RoleEnum.PRODUCT_MANAGER],
  // Намеренно пусто: SUPPORT не может редактировать/удалять балансы трейдеров.
  [ActionKey.TRADER_BALANCES_UPDATE]: [],
  [ActionKey.TRADER_BALANCES_DELETE]: [],
  [ActionKey.TRADER_BALANCES_ADD_FUNDS]: [],

  // Ставки трейдеров доступны для полного управления SUPPORT и PRODUCT_MANAGER.
  [ActionKey.TRADER_RATES_CREATE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [ActionKey.TRADER_RATES_TOGGLE_ACTIVE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [ActionKey.TRADER_RATES_UPDATE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [ActionKey.TRADER_RATES_DELETE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],

  // Выплаты OUT — вердикт по реквизитам: PRODUCT_MANAGER, ADMIN и is_superuser.
  [ActionKey.PAYOUTS_OUT_INVOICE_REQUISITES_VERDICT]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [ActionKey.PAYOUTS_OUT_RESOLVE_SUCCESS]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [ActionKey.PAYOUTS_OUT_RESOLVE_DECLINE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],

  // Транзакции: SUPPORT может просматривать детали.
  [ActionKey.TRANSACTIONS_VIEW_DETAILS]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER, RoleEnum.CLIENT_MANAGER],
  // Preserve OUT success permissions; IN success uses a separate scoped action.
  [ActionKey.TRANSACTIONS_UPDATE_TO_SUCCESS]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [ActionKey.TRANSACTIONS_IN_UPDATE_TO_SUCCESS]: [],
  [ActionKey.TRANSACTIONS_UPDATE_TO_DECLINE]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [ActionKey.TRANSACTIONS_SEND_CALLBACK]: [],
  // IN and appeal success each have one explicit operator grant.
  [ActionKey.APPEALS_RESOLVE_SUCCESS]: [],

  // Реквизиты: PRODUCT_MANAGER может только включать и выключать.
  [ActionKey.REQUISITES_CREATE]: [],
  [ActionKey.REQUISITES_TOGGLE_ACTIVE]: [RoleEnum.PRODUCT_MANAGER],
  [ActionKey.REQUISITES_UPDATE]: [],
  [ActionKey.REQUISITES_DELETE]: [],
  [ActionKey.REQUISITES_UNBLOCK]: [],

  // Финансы — Платёжные системы: намеренно пусто, SUPPORT не может управлять.
  [ActionKey.FINANCE_WALLETS_CREATE]: [],
  [ActionKey.FINANCE_WALLETS_UPDATE]: [],
  [ActionKey.FINANCE_WALLETS_DELETE]: [],

  // Финансы — Банки: намеренно пусто, SUPPORT не может управлять.
  [ActionKey.FINANCE_ACCOUNTS_CREATE]: [],
  [ActionKey.FINANCE_ACCOUNTS_UPDATE]: [],
  [ActionKey.FINANCE_ACCOUNTS_DELETE]: [],

  // Финансы — Валюты: намеренно пусто, SUPPORT не может управлять.
  [ActionKey.FINANCE_CURRENCIES_CREATE]: [],
  [ActionKey.FINANCE_CURRENCIES_UPDATE]: [],
  [ActionKey.FINANCE_CURRENCIES_DELETE]: [],

  // Финансы — Страны: намеренно пусто, SUPPORT не может управлять.
  [ActionKey.FINANCE_COUNTRIES_CREATE]: [],
  [ActionKey.FINANCE_COUNTRIES_UPDATE]: [],
  [ActionKey.FINANCE_COUNTRIES_DELETE]: [],

  // Финансы — Курсы валют: намеренно пусто, SUPPORT не может создавать или удалять курсы.
  [ActionKey.FINANCE_EXCHANGE_RATES_CREATE]: [],
  [ActionKey.FINANCE_EXCHANGE_RATES_DELETE]: [],
};

const TRADER_FULL_ACCESS_ACTIONS = new Set<ActionKey>([
  ActionKey.TRADERS_CREATE,
  ActionKey.TRADERS_TOGGLE_ACTIVE,
  ActionKey.TRADERS_PAYOUT_OUT_ENABLED,
  ActionKey.TRADERS_UPDATE,
  ActionKey.TRADERS_DELETE,
  ActionKey.TRADER_BALANCES_CREATE,
  ActionKey.TRADER_BALANCES_UPDATE,
  ActionKey.TRADER_BALANCES_DELETE,
  ActionKey.TRADER_BALANCES_ADD_FUNDS,
  ActionKey.TRADER_RATES_CREATE,
  ActionKey.TRADER_RATES_TOGGLE_ACTIVE,
  ActionKey.TRADER_RATES_UPDATE,
  ActionKey.TRADER_RATES_DELETE,
  ActionKey.PAYOUTS_OUT_INVOICE_REQUISITES_VERDICT,
  ActionKey.PAYOUTS_OUT_RESOLVE_SUCCESS,
  ActionKey.PAYOUTS_OUT_RESOLVE_DECLINE,
  ActionKey.TRANSACTIONS_VIEW_DETAILS,
  ActionKey.TRANSACTIONS_UPDATE_TO_SUCCESS,
  ActionKey.TRANSACTIONS_UPDATE_TO_DECLINE,
  ActionKey.TRANSACTIONS_SEND_CALLBACK,
  ActionKey.REQUISITES_CREATE,
  ActionKey.REQUISITES_TOGGLE_ACTIVE,
  ActionKey.REQUISITES_UPDATE,
  ActionKey.REQUISITES_DELETE,
]);

const ACTION_PERMISSION: Partial<Record<ActionKey, string>> = {
  [ActionKey.MERCHANTS_CREATE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.MERCHANTS_UPDATE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.MERCHANTS_DELETE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.MERCHANT_BALANCES_CREATE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.MERCHANT_BALANCES_UPDATE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.MERCHANT_BALANCES_DELETE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.MERCHANT_RATES_CREATE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.MERCHANT_RATES_TOGGLE_ACTIVE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.MERCHANT_RATES_UPDATE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.MERCHANT_RATES_DELETE]: Permission.MERCHANTS_MANAGE,
  [ActionKey.OFFERS_CREATE]: Permission.OFFERS_MANAGE,
  [ActionKey.OFFERS_UPDATE]: Permission.OFFERS_MANAGE,
  [ActionKey.OFFERS_DELETE]: Permission.OFFERS_MANAGE,
  [ActionKey.LOSSES_CREATE]: Permission.LOSSES_MANAGE,
  [ActionKey.LOSSES_UPDATE]: Permission.LOSSES_MANAGE,
  [ActionKey.TRADERS_CREATE]: Permission.TRADERS_MANAGE,
  [ActionKey.TRADERS_UPDATE]: Permission.TRADERS_MANAGE,
  [ActionKey.TRADERS_DELETE]: Permission.TRADERS_MANAGE,
  [ActionKey.TRADERS_TOGGLE_ACTIVE]: Permission.TRADERS_MANAGE,
  [ActionKey.TRADERS_PAYOUT_OUT_ENABLED]: Permission.TRADERS_MANAGE,
  [ActionKey.TRANSACTIONS_VIEW_DETAILS]: Permission.TRANSACTIONS_READ,
  [ActionKey.TRANSACTIONS_SEND_CALLBACK]: Permission.TRANSACTIONS_CALLBACKS_SEND,
  [ActionKey.PAYOUTS_OUT_RESOLVE_SUCCESS]: Permission.PAYOUTS_SUPPORT,
  [ActionKey.PAYOUTS_OUT_RESOLVE_DECLINE]: Permission.PAYOUTS_SUPPORT,
  [ActionKey.PAYOUTS_OUT_INVOICE_REQUISITES_VERDICT]: Permission.PAYOUTS_SUPPORT,
  [ActionKey.REQUISITES_CREATE]: Permission.REQUISITES_MANAGE,
  [ActionKey.REQUISITES_UPDATE]: Permission.REQUISITES_MANAGE,
  [ActionKey.REQUISITES_DELETE]: Permission.REQUISITES_MANAGE,
  [ActionKey.REQUISITES_TOGGLE_ACTIVE]: Permission.REQUISITES_MANAGE,
  [ActionKey.REQUISITES_UNBLOCK]: Permission.REQUISITES_MANAGE,
};

export function canPerformAction(user: User | null | undefined, action: ActionKey): boolean {
  if (!user) return false;
  if (hasRevokedAccess(user)) return false;
  if (action === ActionKey.TRANSACTIONS_SEND_CALLBACK) {
    return hasPermission(user, Permission.TRANSACTIONS_CALLBACKS_SEND);
  }
  if (action === ActionKey.REQUISITES_UNBLOCK) {
    return hasPermission(user, Permission.REQUISITES_MANAGE);
  }
  if (action === ActionKey.TRANSACTIONS_IN_UPDATE_TO_SUCCESS) {
    return user.role === RoleEnum.ADMIN || hasExceptionalTransactionSuccessAccess(user);
  }
  if (action === ActionKey.APPEALS_RESOLVE_SUCCESS) {
    return user.role === RoleEnum.ADMIN || hasExceptionalAppealSuccessAccess(user);
  }
  if (user.is_superuser) return true;
  if (user.role === RoleEnum.ADMIN) return true;
  const permission = ACTION_PERMISSION[action];
  if (permission && hasPermission(user, permission)) return true;
  if (hasTraderFullAccess(user) && TRADER_FULL_ACCESS_ACTIONS.has(action)) return true;
  return ACTION_ACCESS_BY_ROLE[action].includes(user.role);
}

